import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  Info,
  Loader2,
  ShieldAlert,
  Trash2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  SaleNormalizationAction,
  SaleNormalizationDirection,
  SaleNormalizationPreview,
  applySaleNormalization,
  previewSaleNormalization,
} from "@/Api/saleNormalization";
import { InconsistentSaleRow } from "../types";

interface Props {
  sale: InconsistentSaleRow | null;
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}

const DIRECTION_LABEL: Record<SaleNormalizationDirection, string> = {
  keep_approval: "A aprovação prevalece",
  keep_rejection: "A recusa prevalece",
};

const DIRECTION_HELP: Record<SaleNormalizationDirection, string> = {
  keep_approval:
    "Remove as etapas deixadas pela recusa, funde as etapas duplicadas e devolve a venda ao fluxo normal.",
  keep_rejection:
    "Sanea as etapas e executa o cancelamento oficial: devolve as cotas ao vendedor, cancela o recebível e restaura reinvestimento, vale compra e PIX antecipado.",
};

const actionIcon = (action: SaleNormalizationAction["action"]) => {
  if (action === "delete_step") return <Trash2 className="h-4 w-4 text-red-600" />;
  if (action === "note") return <Info className="h-4 w-4 text-sky-600" />;
  if (action === "manual_review")
    return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  if (action === "delegate_cancel")
    return <XCircle className="h-4 w-4 text-red-600" />;
  return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
};

/**
 * O apply só é aceito pelo backend com o preview_hash devolvido pelo preview.
 * Por isso qualquer troca de direção ou do "entrega já realizada" refaz o
 * preview e descarta o hash anterior — é o que garante que o administrador
 * confirma exatamente o plano que leu.
 */
export const NormalizeSaleModal = ({ sale, open, onClose, onApplied }: Props) => {
  const [direction, setDirection] = useState<SaleNormalizationDirection>("keep_approval");
  const [finalizeDelivery, setFinalizeDelivery] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState<SaleNormalizationPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;

    setDirection("keep_approval");
    setFinalizeDelivery(false);
    setReason("");
    setConfirmed(false);
    setPreview(null);
  }, [open, sale?.id]);

  useEffect(() => {
    if (!open || !sale) return;

    let cancelled = false;

    const loadPreview = async () => {
      setLoadingPreview(true);
      setConfirmed(false);
      setPreview(null);

      const response = await previewSaleNormalization(sale.id, direction, finalizeDelivery);

      if (cancelled) return;

      if (response.ok) {
        setPreview(response.data?.response ?? null);
      } else {
        toast.error(response.data?.message ?? "Não foi possível gerar a pré-visualização.");
      }

      setLoadingPreview(false);
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [open, sale, direction, finalizeDelivery]);

  const executableActions = useMemo(
    () => (preview?.plan ?? []).filter((action) => action.action !== "note"),
    [preview]
  );

  const needsManualReview = useMemo(
    () => (preview?.plan ?? []).some((action) => action.action === "manual_review"),
    [preview]
  );

  const handleApply = async () => {
    if (!sale || !preview) return;

    setApplying(true);

    const response = await applySaleNormalization(sale.id, {
      direction,
      preview_hash: preview.preview_hash,
      finalize_delivery: finalizeDelivery,
      confirmed: true,
      reason,
    });

    setApplying(false);

    if (!response.ok) {
      toast.error(response.data?.message ?? "Não foi possível normalizar a venda.");
      return;
    }

    toast.success("Venda normalizada com sucesso.");
    onApplied();
    onClose();
  };

  const canApply =
    !!preview && confirmed && reason.trim().length >= 5 && !loadingPreview && !applying;

  return (
    <Dialog open={open} onOpenChange={(value) => (!value ? onClose() : undefined)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Normalizar venda #{sale?.id}
          </DialogTitle>
          <DialogDescription>
            {sale?.client} · {sale?.seller} · {sale?.unity} · status atual:{" "}
            {sale?.sale_status}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="mb-2 block">Qual lado prevalece?</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {(["keep_approval", "keep_rejection"] as SaleNormalizationDirection[]).map(
                (option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setDirection(option)}
                    className={`rounded-md border p-3 text-left text-sm transition-colors ${
                      direction === option
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted"
                    }`}
                  >
                    <span className="font-medium">{DIRECTION_LABEL[option]}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {DIRECTION_HELP[option]}
                    </span>
                  </button>
                )
              )}
            </div>
          </div>

          {direction === "keep_approval" && (
            <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
              <Checkbox
                checked={finalizeDelivery}
                onCheckedChange={(value) => setFinalizeDelivery(value === true)}
                className="mt-0.5"
              />
              <span>
                A entrega já foi realizada — finalizar as etapas pendentes e fechar a
                venda como Aprovada.
                <span className="mt-1 block text-xs text-muted-foreground">
                  Marque apenas se o cliente realmente recebeu. Só com a venda Aprovada
                  ela entra na comissão do vendedor.
                </span>
              </span>
            </label>
          )}

          <div>
            <Label className="mb-2 block">
              O que será feito
              {loadingPreview && (
                <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin" />
              )}
            </Label>

            {loadingPreview && !preview && (
              <p className="text-sm text-muted-foreground">Montando o plano…</p>
            )}

            {preview && (
              <ul className="space-y-2">
                {preview.plan.map((action, index) => (
                  <li
                    key={`${action.action}-${action.id ?? index}`}
                    className="flex gap-2 rounded-md border border-border p-2 text-sm"
                  >
                    <span className="mt-0.5 shrink-0">{actionIcon(action.action)}</span>
                    <span>{action.description}</span>
                  </li>
                ))}
              </ul>
            )}

            {preview && executableActions.length === 0 && (
              <p className="mt-2 text-sm text-muted-foreground">
                Nenhuma alteração a aplicar nesta direção.
              </p>
            )}
          </div>

          {needsManualReview && (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                O plano tem pontos marcados para conferência manual. Revise antes de
                aplicar.
              </span>
            </div>
          )}

          <div>
            <Label htmlFor="normalization-reason" className="mb-2 block">
              Motivo da correção (fica na auditoria)
            </Label>
            <Textarea
              id="normalization-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Ex.: venda 210111 recusada e aprovada ao mesmo tempo; a aprovação foi confirmada com o gerente da unidade."
              rows={3}
            />
          </div>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={confirmed}
              onCheckedChange={(value) => setConfirmed(value === true)}
              className="mt-0.5"
              disabled={!preview || loadingPreview}
            />
            <span>Li o plano acima e confirmo a normalização desta venda.</span>
          </label>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={applying}>
            Cancelar
          </Button>
          <Button onClick={handleApply} disabled={!canApply}>
            {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Aplicar normalização
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NormalizeSaleModal;
